import React, { useEffect, useRef, useState } from "react";
import SbEditable from "storyblok-react";
import PowerButton from "static/power_button.svg";
import RichText from "src/utils/RichText";
import WhiteBrushStroke from "static/white-brush-stroke.png";

const AnimatedServices = ({ blok }) => {
  const itemsRef = useRef([]);
  const titleRef = useRef(null);
  const brushStrokeRef = useRef(null);
  const [titleVisible, setTitleVisible] = useState(false);
  const [brushStrokeVisible, setBrushStrokeVisible] = useState(false);

  useEffect(() => {
    const titleObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTitleVisible(true);
            titleObserver.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    if (titleRef.current) {
      titleObserver.observe(titleRef.current);
    }

    return () => {
      titleObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const brushObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setBrushStrokeVisible(true);
            brushObserver.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (brushStrokeRef.current) {
      brushObserver.observe(brushStrokeRef.current);
    }

    return () => {
      brushObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      itemsRef.current.forEach((el) => {
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const animationStart = windowHeight;
        const animationEnd = windowHeight * 0.7;

        let scrollProgress = Math.min(
          Math.max(
            0,
            1 - (rect.top - animationEnd) / (animationStart - animationEnd)
          ),
          1
        );

        const itemOne = el.querySelector(".item-one-animation");
        const itemTwo = el.querySelector(".item-two-animation");

        if (itemOne) {
          itemOne.style.transform = `translateX(${
            (-1 + scrollProgress) * 100
          }%)`;
        }

        if (itemTwo) {
          itemTwo.style.transform = `translateX(${
            (1 - scrollProgress) * 100
          }%)`;
        }
      });

      requestAnimationFrame(handleScroll);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <SbEditable content={blok} key={blok._uid}>
      <div
        id="what-we-do"
        className="bg-electric_blue relative overflow-hidden flex"
      >
        <div className="my-[60px] lg:my-20 w-full flex flex-col items-center justify-center px-6 lg:px-0">
          {/* Title section */}
          <div className="container mb-12 lg:mb-20">
            <div className="max-w-xl mx-auto text-center">
              <div
                ref={titleRef}
                className={`transition-all duration-1000 ${
                  titleVisible
                    ? "translate-y-0 opacity-100"
                    : "translate-y-10 opacity-0"
                }`}
              >
                {blok.power_icon?.filename && (
                  <div class="mx-auto flex justify-center items-center mb-5 lg:mb-7">
                  <img
                    src={blok.power_icon.filename}
                    alt={blok.power_icon.alt}
                    className="w-10 h-10"
                  />
                  </div>
                )}
                <div className="h2 text-[26px] leading-[32px] lg:leading-[55px] lg:text-[45px] text-white mb-5 lg:mb-8">
                  <RichText>{blok.title}</RichText>
                </div>
              </div>

              <div
                ref={brushStrokeRef}
                className={`transition-all duration-1000 ease-out ${
                  brushStrokeVisible
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 -translate-x-full"
                }`}
              >
                <img
                  src={WhiteBrushStroke}
                  alt="brush-stroke"
                  className="mx-auto"
                />
              </div>
            </div>
          </div>

          {blok.items?.map((item, index) => (
            <div
              ref={(el) => (itemsRef.current[index] = el)}
              className={`grid w-full grid-cols-[minmax(120px,1fr)_40px_minmax(120px,1fr)] lg:grid-cols-[minmax(120px,1fr)_60px_minmax(120px,1fr)]
                my-2 lg:my-4 items-center text-white font-extrabold text-[18px] lg:text-4.5xl leading-normal lg:leading-[45px]`}
              key={item.id}
            >
              {/* item_one animation */}
              <div className="relative w-full">
                <div className="relative item-one-animation text-end duration-300 transition-all my-auto">
                  {item.item_one}
                </div>
              </div>

              {/* Power Button */}
              <div className="w-full flex items-center justify-center">
                <img
                  src={PowerButton}
                  alt="power-button"
                  className="w-[18px] h-[18px] lg:w-8 lg:h-8"
                />
              </div>

              {/* item_two animation */}
              <div className="relative w-full">
                <div className="relative item-two-animation duration-300 transition-all">
                  {item.item_two}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SbEditable>
  );
};

export default AnimatedServices;
